import { useState } from 'react'
import * as RadixSlider from '@radix-ui/react-slider'
import cx from 'classnames'

import SliderInput from './sliderInput'

type SliderValue = number | number[]

interface Props {
  value?: SliderValue
  defaultValue?: SliderValue
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  range?: boolean
  allowCross?: boolean
  minLabel?: string
  maxLabel?: string
  onChange?: (value: SliderValue) => void
  marks?: unknown
  dots?: unknown
  included?: unknown
  className?: string
}

const toArray = (v: SliderValue | undefined): number[] | undefined => {
  if (v === undefined) {
    return undefined
  }
  return Array.isArray(v) ? v : [v]
}

const Slider = ({
  value,
  defaultValue,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  range,
  allowCross = true,
  minLabel,
  maxLabel,
  onChange,
}: Props) => {
  const isRange = range || Array.isArray(value) || Array.isArray(defaultValue)
  const isInputHidden = minLabel !== undefined || maxLabel !== undefined

  const currentValue = toArray(value)
  const currentDefault = toArray(defaultValue) ?? [isRange ? min : min]

  const [internalMin, setInternalMin] = useState<number>(
    currentValue?.[0] ?? currentDefault[0] ?? min
  )
  const [internalMax, setInternalMax] = useState<number>(
    currentValue?.[1] ?? currentDefault[1] ?? max
  )
  const [previousValue, setPreviousValue] = useState(value)

  if (previousValue !== value) {
    setPreviousValue(value)
    const arr = toArray(value)
    if (arr) {
      setInternalMin(arr[0])
      if (isRange && arr[1] !== undefined) {
        setInternalMax(arr[1])
      }
    }
  }

  const radixValue: number[] = (() => {
    if (currentValue) {
      return currentValue
    }
    if (isRange) {
      return [internalMin, internalMax]
    }
    return [internalMin]
  })()

  const handleValueChange = (vals: number[]) => {
    if (disabled) {
      return
    }
    setInternalMin(vals[0])
    if (isRange && vals[1] !== undefined) {
      setInternalMax(vals[1])
    }
    onChange?.(isRange ? vals : vals[0])
  }

  return (
    <div className="updated-component flex flex-col">
      <div className="flex items-stretch justify-between text-xs mb-4 text-coolGray-800">
        <div>{minLabel ?? min}</div>
        <div>{maxLabel ?? max}</div>
      </div>
      <RadixSlider.Root
        value={radixValue}
        defaultValue={currentDefault}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        minStepsBetweenThumbs={allowCross ? 0 : 1}
        onValueChange={handleValueChange}
        className={cx(
          'relative flex items-center select-none touch-none w-full',
          { 'opacity-75 cursor-not-allowed': disabled }
        )}
      >
        <RadixSlider.Track className="bg-coolGray-200 relative grow rounded-full h-3">
          <RadixSlider.Range className="absolute bg-maroon-400 rounded-full h-full" />
        </RadixSlider.Track>
        <RadixSlider.Thumb className="block w-5 h-5 bg-white border border-coolGray-300 rounded-full shadow focus:outline-none focus:ring-2 focus:ring-maroon-400" />
        {isRange && (
          <RadixSlider.Thumb className="block w-5 h-5 bg-white border border-coolGray-300 rounded-full shadow focus:outline-none focus:ring-2 focus:ring-maroon-400" />
        )}
      </RadixSlider.Root>
      {!isInputHidden && (
        <div className="w-min mt-3 flex items-center">
          <SliderInput
            aria-label="min"
            disabled={disabled}
            onChange={(val) => {
              if (!disabled && val !== undefined) {
                setInternalMin(val)
                if (isRange) {
                  onChange?.([val, internalMax])
                } else {
                  onChange?.(val)
                }
              }
            }}
            value={
              isRange
                ? internalMin
                : typeof value === 'number'
                  ? value
                  : internalMin
            }
            min={min}
            max={isRange ? internalMax : max}
          />
          {isRange && (
            <>
              <span className="text-coolGray-700">-</span>
              <SliderInput
                aria-label="max"
                disabled={disabled}
                onChange={(val) => {
                  setInternalMax(val ?? max)
                  val !== undefined && onChange?.([internalMin, val])
                }}
                value={internalMax}
                min={internalMin}
                max={max}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Slider
